import InputFile from "@components/Atoms/InputFile";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ButtonStateService from "@core-ui/ContextServices/ButtonStateService/ButtonStateService";
import ResourceService, { type ResourceServiceType } from "@core-ui/ContextServices/ResourceService/ResourceService";
import t from "@ext/localization/locale/translate";
import type { Editor } from "@tiptap/core";
import { DropdownMenuRadioGroup, DropdownMenuRadioItem } from "@ui-kit/Dropdown";
import { GlassToolbarIcon, GlassToolbarToggleButton } from "@ui-kit/GlassToolbar";
import { Icon } from "@ui-kit/Icon";
import { type ChangeEvent, useCallback } from "react";
import createFile from "../logic/createFile";

interface FileMenuButtonProps {
	editor: Editor;
	onStart?: () => void;
	onSave?: () => void;
}

interface FileInputProps extends Pick<FileMenuButtonProps, "editor" | "onSave"> {
	apiUrlCreator: ApiUrlCreator;
	resourceService: ResourceServiceType;
	children: JSX.Element;
}

const FileInput = ({ editor, onSave, apiUrlCreator, resourceService, children }: FileInputProps) => {
	const onAbort = useCallback(() => {
		onSave?.();
	}, [onSave]);

	const onChange = useCallback(
		async (event: ChangeEvent<HTMLInputElement>) => {
			const filesArray = Array.from(event.currentTarget.files);
			await createFile(filesArray, editor.view, apiUrlCreator, resourceService);
			event.target.value = "";
			onSave?.();
		},
		[editor.view, apiUrlCreator, resourceService, onSave],
	);

	return (
		<InputFile className="flex flex-row items-center w-full cursor-pointer" onAbort={onAbort} onChange={onChange}>
			{children}
		</InputFile>
	);
};

export const FileMenuButton = ({ editor, onStart, onSave }: FileMenuButtonProps) => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const rs = ResourceService.value;

	const { disabled, isActive } = ButtonStateService.useCurrentAction({ mark: "file" });
	return (
		<GlassToolbarToggleButton
			active={isActive}
			data-qa={`qa-edit-menu-file`}
			disabled={disabled}
			focusable
			onSelect={(e) => {
				e.preventDefault();
				onStart?.();
			}}
			tooltipText={t("file")}
		>
			<FileInput apiUrlCreator={apiUrlCreator} editor={editor} onSave={onSave} resourceService={rs}>
				<GlassToolbarIcon icon="file" />
			</FileInput>
		</GlassToolbarToggleButton>
	);
};

export const FileMenuButtonDropdown = ({ editor, onStart, onSave }: FileMenuButtonProps) => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const rs = ResourceService.value;

	const { disabled, isActive } = ButtonStateService.useCurrentAction({ mark: "file" });

	if (disabled) {
		return (
			<DropdownMenuRadioGroup value={isActive ? "file" : undefined}>
				<DropdownMenuRadioItem disabled={disabled} value="file">
					<div className="flex flex-row items-center gap-2 w-full" data-qa="qa-edit-menu-file">
						<Icon icon="file" />
						{t("file")}
					</div>
				</DropdownMenuRadioItem>
			</DropdownMenuRadioGroup>
		);
	}

	return (
		<DropdownMenuRadioGroup value={isActive ? "file" : undefined}>
			<DropdownMenuRadioItem
				disabled={disabled}
				onSelect={(e) => {
					e.preventDefault();
					onStart?.();
				}}
				value="file"
			>
				<FileInput apiUrlCreator={apiUrlCreator} editor={editor} onSave={onSave} resourceService={rs}>
					<div className="flex flex-row items-center gap-2 w-full" data-qa="qa-edit-menu-file">
						<Icon icon="file" />
						{t("file")}
					</div>
				</FileInput>
			</DropdownMenuRadioItem>
		</DropdownMenuRadioGroup>
	);
};
